// Путь: D:\QR\QR-WEB\dashboard\src\components\Business\BusinessContacts.jsx
// Название: Business Contacts - Smart Forms + CSV Import
// Версия: 4.0.0
// Дата: 27.12.2025

import React, { useState, useEffect, useRef } from 'react';
import { api } from '../../services/api';
import './BusinessContacts.css';

const BusinessContacts = ({ contactType }) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [sortField, setSortField] = useState('first_name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [stats, setStats] = useState({ employees: 0, customers: 0, leads: 0, total: 0 });
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [csvContent, setCsvContent] = useState('');
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);
  const csvInputRef = useRef(null);

  // ============================================
  // СЕКЦИЯ: Конфиг по типу
  // ============================================
  const typeConfig = {
    employees: {
      title: '👔 Сотрудники',
      subtitle: 'База сотрудников для B2B кампаний',
      type: 'employee',
      icon: '👔',
      emptyIcon: '👔',
      emptyTitle: 'Добавьте первого сотрудника',
      emptyText: 'Сотрудники получат персональные AI-подарки',
      tableColumns: ['photo', 'name', 'email', 'phone', 'department', 'position'],
      formFields: ['photo', 'name', 'email', 'phone', 'department', 'position', 'birthday', 'notes'],
      requiredFields: ['first_name'],
      namePlaceholder: 'Jean',
      lastNamePlaceholder: 'Dupont'
    },
    customers: {
      title: '🛒 Клиенты',
      subtitle: 'Существующие клиенты для Brand кампаний',
      type: 'customer',
      icon: '🛒',
      emptyIcon: '🛒',
      emptyTitle: 'Добавьте первого клиента',
      emptyText: 'Клиенты получат персональные предложения',
      tableColumns: ['photo', 'name', 'email', 'phone', 'company', 'source'],
      formFields: ['photo', 'name', 'email', 'phone', 'company', 'source', 'notes'],
      requiredFields: ['first_name', 'email'],
      namePlaceholder: 'Marie',
      lastNamePlaceholder: 'Laurent'
    },
    leads: {
      title: '🎯 Лиды',
      subtitle: 'Потенциальные клиенты для привлечения',
      type: 'lead',
      icon: '🎯',
      emptyIcon: '🎯',
      emptyTitle: 'Добавьте первый лид',
      emptyText: 'Email-адреса для рассылки акций и бонусов',
      tableColumns: ['name', 'email', 'phone', 'source', 'tags'],
      formFields: ['email', 'name_optional', 'phone', 'source', 'tags', 'notes'],
      requiredFields: ['email'],
      namePlaceholder: 'Имя (опционально)',
      lastNamePlaceholder: ''
    },
    default: {
      title: '📇 Все контакты',
      subtitle: 'Полная база контактов',
      type: null,
      icon: '📇',
      emptyIcon: '📇',
      emptyTitle: 'База контактов пуста',
      emptyText: 'Добавьте контакты для кампаний',
      tableColumns: ['photo', 'name', 'email', 'phone', 'type'],
      formFields: ['photo', 'name', 'email', 'phone', 'department', 'company', 'notes'],
      requiredFields: ['first_name'],
      namePlaceholder: 'Jean',
      lastNamePlaceholder: 'Dupont'
    }
  };

  const config = typeConfig[contactType] || typeConfig.default;

  const getEmptyForm = () => ({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    department: '',
    company: '',
    position: '',
    birthday: '',
    source: '',
    tags: '',
    notes: '',
    photo_url: '',
    contact_type: config.type || 'employee'
  });

  const [form, setForm] = useState(getEmptyForm());

  useEffect(() => {
    loadContacts();
  }, [contactType]);

  // ============================================
  // СЕКЦИЯ: Загрузка данных
  // ============================================
  const loadContacts = async () => {
    try {
      setLoading(true);
      let url = '/contacts';
      if (config.type) {
        url += '?contact_type=' + config.type;
      }
      const response = await api.get(url);
      setContacts(response.data || []);
      if (response.stats) {
        setStats(response.stats);
      }
    } catch (error) {
      console.error('Load contacts error:', error);
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // СЕКЦИЯ: CRUD операции
  // ============================================
  const saveContact = async () => {
    // Валидация по типу
    if (config.type === 'lead') {
      if (!form.email.trim() && !form.phone.trim()) {
        alert('Укажите Email или Телефон');
        return;
      }
    } else {
      if (!form.first_name.trim()) {
        alert('Введите имя контакта');
        return;
      }
    }
    
    try {
      setUploading(true);
      const data = { ...form, contact_type: config.type || form.contact_type };
      
      // Для лидов без имени - ставим email как имя
      if (config.type === 'lead' && !data.first_name.trim()) {
        data.first_name = data.email.split('@')[0] || 'Lead';
        data.last_name = '';
      }
      
      if (editingContact) {
        await api.put('/contacts/' + editingContact.id, data);
        alert('✅ Контакт обновлён');
      } else {
        await api.post('/contacts', data);
        alert('✅ Контакт добавлен');
      }
      closeModal();
      loadContacts();
    } catch (error) {
      alert('❌ Ошибка: ' + (error.message || 'Не удалось сохранить'));
    } finally {
      setUploading(false);
    }
  };

  const deleteContact = async (id, name) => {
    if (!window.confirm('Удалить контакт "' + name + '"?')) return;
    try {
      await api.delete('/contacts/' + id);
      loadContacts();
    } catch (error) {
      alert('❌ Ошибка: ' + (error.message || 'Не удалось удалить'));
    }
  };

  const deleteSelected = async () => {
    if (selectedContacts.length === 0) return;
    if (!window.confirm('Удалить ' + selectedContacts.length + ' контактов?')) return;
    
    try {
      await api.delete('/contacts', { data: { ids: selectedContacts } });
      setSelectedContacts([]);
      loadContacts();
    } catch (error) {
      alert('❌ Ошибка при удалении');
    }
  };

  // Конвертация лида в клиента
  const convertToCustomer = async (contact) => {
    if (!window.confirm('Конвертировать "' + (contact.first_name || contact.email) + '" в клиента?')) return;
    try {
      await api.post('/contacts/' + contact.id + '/convert', {});
      alert('✅ Лид конвертирован в клиента');
      loadContacts();
    } catch (error) {
      alert('❌ Ошибка конвертации');
    }
  };

  // ============================================
  // СЕКЦИЯ: Модальное окно
  // ============================================
  const openAddModal = () => {
    setEditingContact(null);
    setForm(getEmptyForm());
    setPhotoPreview(null);
    setShowModal(true);
  };

  const openEditModal = (contact) => {
    setEditingContact(contact);
    setForm({
      first_name: contact.first_name || '',
      last_name: contact.last_name || '',
      email: contact.email || '',
      phone: contact.phone || '',
      department: contact.department || '',
      company: contact.company || '',
      position: contact.position || '',
      birthday: contact.birthday ? contact.birthday.split('T')[0] : '',
      source: contact.source || '',
      tags: contact.tags || '',
      notes: contact.notes || '',
      photo_url: contact.photo_url || '',
      contact_type: contact.contact_type || 'employee'
    });
    setPhotoPreview(contact.photo_url || null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingContact(null);
    setPhotoPreview(null);
  };

  // ============================================
  // СЕКЦИЯ: Работа с фото
  // ============================================
  const handlePhotoFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { alert('Выберите изображение'); return; }
    if (file.size > 5 * 1024 * 1024) { alert('Файл слишком большой (макс. 5MB)'); return; }

    const reader = new FileReader();
    reader.onload = (event) => {
      setForm({ ...form, photo_url: event.target.result });
      setPhotoPreview(event.target.result);
    };
    reader.readAsDataURL(file);
  };

  const removePhoto = () => {
    setForm({ ...form, photo_url: '' });
    setPhotoPreview(null);
  };

  // ============================================
  // СЕКЦИЯ: CSV Import
  // ============================================
  const handleCsvFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      setCsvContent(event.target.result);
      setImportResult(null);
    };
    reader.readAsText(file, 'UTF-8');
  };

  const importCsv = async () => {
    if (!csvContent.trim()) {
      alert('Загрузите CSV файл');
      return;
    }
    
    try {
      setImporting(true);
      const response = await api.post('/contacts/import-csv', {
        csvContent,
        contact_type: config.type || 'employee'
      });
      setImportResult(response.data);
      loadContacts();
    } catch (error) {
      alert('❌ Ошибка: ' + (error.message || 'Не удалось импортировать'));
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = () => {
    const type = config.type || 'employee';
    window.open(`https://qr-money.onrender.com/api/contacts/csv-template/${type}`, '_blank');
  };

  const closeImportModal = () => {
    setShowImportModal(false);
    setCsvContent('');
    setImportResult(null);
    if (csvInputRef.current) csvInputRef.current.value = '';
  };

  // ============================================
  // СЕКЦИЯ: Сортировка и фильтрация
  // ============================================
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const toggleSelect = (id) => {
    setSelectedContacts(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedContacts.length === filteredContacts.length) {
      setSelectedContacts([]);
    } else {
      setSelectedContacts(filteredContacts.map(c => c.id));
    }
  };

  let filteredContacts = contacts.filter(c => {
    if (search) {
      const s = search.toLowerCase();
      return (c.first_name || '').toLowerCase().includes(s) ||
             (c.last_name || '').toLowerCase().includes(s) ||
             (c.email || '').toLowerCase().includes(s) ||
             (c.company || '').toLowerCase().includes(s);
    }
    return true;
  });

  filteredContacts = [...filteredContacts].sort((a, b) => {
    const aVal = (a[sortField] || '').toString().toLowerCase();
    const bVal = (b[sortField] || '').toString().toLowerCase();
    return sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
  });

  const getTypeLabel = (type) => {
    const types = {
      employee: { label: 'Сотрудник', icon: '👔', class: 'type-employee' },
      customer: { label: 'Клиент', icon: '🛒', class: 'type-customer' },
      lead: { label: 'Лид', icon: '🎯', class: 'type-lead' }
    };
    return types[type] || types.employee;
  };

  const getDisplayName = (contact) => {
    if (contact.first_name || contact.last_name) {
      return ((contact.first_name || '') + ' ' + (contact.last_name || '')).trim();
    }
    return contact.email || 'Без имени';
  };

  // ============================================
  // СЕКЦИЯ: Проверка валидности формы
  // ============================================
  const isFormValid = () => {
    if (config.type === 'lead') {
      return form.email.trim() || form.phone.trim();
    }
    return form.first_name.trim();
  };

  // ============================================
  // СЕКЦИЯ: Рендер - Loading
  // ============================================
  if (loading) {
    return (
      <div className="contacts-page">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Загрузка контактов...</p>
        </div>
      </div>
    );
  }

  // ============================================
  // СЕКЦИЯ: Рендер
  // ============================================
  return (
    <div className="contacts-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-info">
          <h1>{config.title}</h1>
          <p>{config.subtitle}</p>
        </div>
        <div className="header-actions">
          <button className="btn-refresh" onClick={loadContacts}>↻ Обновить</button>
          <button className="btn-secondary" onClick={() => setShowImportModal(true)}>📥 CSV</button>
          <button className="btn-primary" onClick={openAddModal}>+ Добавить</button>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-bar">
        <div className={'stat-item' + (contactType === 'employees' ? ' active' : '')}>
          <span className="stat-icon">👔</span>
          <div className="stat-info">
            <span className="stat-value">{stats.employees || 0}</span>
            <span className="stat-label">Сотрудники</span>
          </div>
        </div>
        <div className={'stat-item' + (contactType === 'customers' ? ' active' : '')}>
          <span className="stat-icon">🛒</span>
          <div className="stat-info">
            <span className="stat-value">{stats.customers || 0}</span>
            <span className="stat-label">Клиенты</span>
          </div>
        </div>
        <div className={'stat-item' + (contactType === 'leads' ? ' active' : '')}>
          <span className="stat-icon">🎯</span>
          <div className="stat-info">
            <span className="stat-value">{stats.leads || 0}</span>
            <span className="stat-label">Лиды</span>
          </div>
        </div>
        <div className="stat-item stat-total">
          <span className="stat-icon">📇</span>
          <div className="stat-info">
            <span className="stat-value">{stats.total || 0}</span>
            <span className="stat-label">Всего</span>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="content-toolbar">
        <div className="toolbar-left">
          <div className="search-box">
            <span className="search-icon">🔍</span>
            <input type="text" placeholder="Поиск..." value={search} onChange={(e) => setSearch(e.target.value)} />
            {search && <button className="search-clear" onClick={() => setSearch('')}>×</button>}
          </div>
        </div>
        <div className="toolbar-right">
          {selectedContacts.length > 0 && (
            <button className="btn-danger" onClick={deleteSelected}>🗑️ Удалить ({selectedContacts.length})</button>
          )}
          <span className="results-count">Найдено: {filteredContacts.length}</span>
        </div>
      </div>

      {/* Table or Empty */}
      {filteredContacts.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">{config.emptyIcon}</div>
          <h3>{config.emptyTitle}</h3>
          <p>{config.emptyText}</p>
          <div className="empty-actions">
            <button className="btn-primary" onClick={openAddModal}>+ Добавить</button>
            <button className="btn-secondary" onClick={() => setShowImportModal(true)}>📥 Импорт CSV</button>
          </div>
        </div>
      ) : (
        <div className="table-container">
          <table className="contacts-table">
            <thead>
              <tr>
                <th className="col-checkbox">
                  <input type="checkbox" checked={selectedContacts.length === filteredContacts.length && filteredContacts.length > 0} onChange={toggleSelectAll} />
                </th>
                {config.tableColumns.includes('photo') && <th className="col-photo">Фото</th>}
                <th className="col-sortable" onClick={() => handleSort('first_name')}>
                  {config.type === 'lead' ? 'Контакт' : 'Имя'} {sortField === 'first_name' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                {config.tableColumns.includes('email') && <th>Email</th>}
                {config.tableColumns.includes('phone') && <th>Телефон</th>}
                {config.tableColumns.includes('department') && <th>Отдел</th>}
                {config.tableColumns.includes('position') && <th>Должность</th>}
                {config.tableColumns.includes('company') && <th>Компания</th>}
                {config.tableColumns.includes('source') && <th>Источник</th>}
                {config.tableColumns.includes('tags') && <th>Теги</th>}
                {config.tableColumns.includes('type') && <th>Тип</th>}
                <th className="col-actions">Действия</th>
              </tr>
            </thead>
            <tbody>
              {filteredContacts.map(contact => {
                const typeInfo = getTypeLabel(contact.contact_type);
                return (
                  <tr key={contact.id} className={selectedContacts.includes(contact.id) ? 'selected' : ''}>
                    <td className="col-checkbox">
                      <input type="checkbox" checked={selectedContacts.includes(contact.id)} onChange={() => toggleSelect(contact.id)} />
                    </td>
                    {config.tableColumns.includes('photo') && (
                      <td className="col-photo">
                        {contact.photo_url ? (
                          <img src={contact.photo_url} alt="" className="contact-photo" />
                        ) : (
                          <div className="contact-photo-placeholder">
                            {(contact.first_name || '?')[0]}{(contact.last_name || '')[0] || ''}
                          </div>
                        )}
                      </td>
                    )}
                    <td>
                      <div className="contact-name">
                        <strong>{getDisplayName(contact)}</strong>
                      </div>
                    </td>
                    {config.tableColumns.includes('email') && <td className="col-email">{contact.email || '—'}</td>}
                    {config.tableColumns.includes('phone') && <td>{contact.phone || '—'}</td>}
                    {config.tableColumns.includes('department') && <td>{contact.department || '—'}</td>}
                    {config.tableColumns.includes('position') && <td>{contact.position || '—'}</td>}
                    {config.tableColumns.includes('company') && <td>{contact.company || '—'}</td>}
                    {config.tableColumns.includes('source') && <td><span className="source-badge">{contact.source || '—'}</span></td>}
                    {config.tableColumns.includes('tags') && <td><span className="tags-badge">{contact.tags || '—'}</span></td>}
                    {config.tableColumns.includes('type') && (
                      <td><span className={'contact-type-badge ' + typeInfo.class}>{typeInfo.icon} {typeInfo.label}</span></td>
                    )}
                    <td>
                      <div className="actions-buttons">
                        <button className="action-btn" onClick={() => openEditModal(contact)} title="Редактировать">✏️</button>
                        {config.type === 'lead' && (
                          <button className="action-btn action-convert" onClick={() => convertToCustomer(contact)} title="В клиента">🛒</button>
                        )}
                        <button className="action-btn action-delete" onClick={() => deleteContact(contact.id, getDisplayName(contact))} title="Удалить">🗑️</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal - Add/Edit Contact */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingContact ? '✏️ Редактировать' : '➕ Новый ' + (config.type === 'lead' ? 'лид' : config.type === 'customer' ? 'клиент' : 'сотрудник')}</h2>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>
            
            <div className="modal-body">
              {/* ФОРМА ДЛЯ СОТРУДНИКОВ */}
              {config.type === 'employee' && (
                <>
                  <div className="photo-section">
                    <div className="photo-preview">
                      {photoPreview ? <img src={photoPreview} alt="Preview" /> : <div className="photo-placeholder"><span>📷</span></div>}
                    </div>
                    <div className="photo-actions">
                      <button className="btn-secondary" onClick={() => fileInputRef.current?.click()}>📁 Загрузить фото</button>
                      <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhotoFile} />
                      {photoPreview && <button className="btn-text" onClick={removePhoto}>❌ Удалить</button>}
                      <p className="photo-hint">Фото используется для AI-генерации подарков</p>
                    </div>
                  </div>
                  <div className="form-section">
                    <div className="form-row">
                      <div className="form-group">
                        <label>Имя *</label>
                        <input type="text" value={form.first_name} onChange={(e) => setForm({...form, first_name: e.target.value})} placeholder="Jean" />
                      </div>
                      <div className="form-group">
                        <label>Фамилия</label>
                        <input type="text" value={form.last_name} onChange={(e) => setForm({...form, last_name: e.target.value})} placeholder="Dupont" />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>📧 Email</label>
                        <input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} placeholder="jean@company.fr" />
                      </div>
                      <div className="form-group">
                        <label>📱 Телефон</label>
                        <input type="text" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} placeholder="+33 6 12 34 56 78" />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>🏢 Отдел</label>
                        <input type="text" value={form.department} onChange={(e) => setForm({...form, department: e.target.value})} placeholder="Marketing" />
                      </div>
                      <div className="form-group">
                        <label>💼 Должность</label>
                        <input type="text" value={form.position} onChange={(e) => setForm({...form, position: e.target.value})} placeholder="Manager" />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>🎂 День рождения</label>
                        <input type="date" value={form.birthday} onChange={(e) => setForm({...form, birthday: e.target.value})} />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>📝 Заметки</label>
                      <textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} placeholder="Доп. информация..." rows={2} />
                    </div>
                  </div>
                </>
              )}

              {/* ФОРМА ДЛЯ КЛИЕНТОВ */}
              {config.type === 'customer' && (
                <>
                  <div className="photo-section photo-section-small">
                    <div className="photo-preview photo-preview-small">
                      {photoPreview ? <img src={photoPreview} alt="Preview" /> : <div className="photo-placeholder"><span>👤</span></div>}
                    </div>
                    <div className="photo-actions">
                      <button className="btn-secondary btn-sm" onClick={() => fileInputRef.current?.click()}>📁 Фото</button>
                      <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhotoFile} />
                      {photoPreview && <button className="btn-text" onClick={removePhoto}>❌</button>}
                    </div>
                  </div>
                  <div className="form-section">
                    <div className="form-row">
                      <div className="form-group">
                        <label>Имя *</label>
                        <input type="text" value={form.first_name} onChange={(e) => setForm({...form, first_name: e.target.value})} placeholder="Marie" />
                      </div>
                      <div className="form-group">
                        <label>Фамилия</label>
                        <input type="text" value={form.last_name} onChange={(e) => setForm({...form, last_name: e.target.value})} placeholder="Laurent" />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>📧 Email *</label>
                        <input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} placeholder="marie@client.fr" />
                      </div>
                      <div className="form-group">
                        <label>📱 Телефон</label>
                        <input type="text" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} placeholder="+33 6 12 34 56 78" />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>🏛️ Компания клиента</label>
                        <input type="text" value={form.company} onChange={(e) => setForm({...form, company: e.target.value})} placeholder="Société XYZ" />
                      </div>
                      <div className="form-group">
                        <label>📍 Источник</label>
                        <select value={form.source} onChange={(e) => setForm({...form, source: e.target.value})}>
                          <option value="">Выберите...</option>
                          <option value="website">Сайт</option>
                          <option value="referral">Рекомендация</option>
                          <option value="social">Соцсети</option>
                          <option value="ads">Реклама</option>
                          <option value="event">Мероприятие</option>
                          <option value="other">Другое</option>
                        </select>
                      </div>
                    </div>
                    <div className="form-group">
                      <label>📝 Заметки</label>
                      <textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} placeholder="История взаимодействия..." rows={2} />
                    </div>
                  </div>
                </>
              )}

              {/* ФОРМА ДЛЯ ЛИДОВ - МИНИМАЛЬНАЯ */}
              {config.type === 'lead' && (
                <div className="form-section lead-form">
                  <div className="lead-form-header">
                    <p>💡 Минимум данных - Email или Телефон</p>
                  </div>
                  <div className="form-group">
                    <label>📧 Email *</label>
                    <input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} placeholder="lead@example.com" className="input-large" />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>👤 Имя</label>
                      <input type="text" value={form.first_name} onChange={(e) => setForm({...form, first_name: e.target.value})} placeholder="Имя" />
                    </div>
                    <div className="form-group">
                      <label>👤 Фамилия</label>
                      <input type="text" value={form.last_name} onChange={(e) => setForm({...form, last_name: e.target.value})} placeholder="Фамилия" />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>📱 Телефон</label>
                    <input type="text" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} placeholder="+33 6 XX XX XX XX" />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>📍 Источник</label>
                      <select value={form.source} onChange={(e) => setForm({...form, source: e.target.value})}>
                        <option value="">Откуда пришёл?</option>
                        <option value="landing">Landing page</option>
                        <option value="facebook">Facebook Ads</option>
                        <option value="google">Google Ads</option>
                        <option value="instagram">Instagram</option>
                        <option value="linkedin">LinkedIn</option>
                        <option value="email_campaign">Email рассылка</option>
                        <option value="referral">Реферал</option>
                        <option value="organic">Органический</option>
                        <option value="other">Другое</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>🏷️ Теги</label>
                      <input type="text" value={form.tags} onChange={(e) => setForm({...form, tags: e.target.value})} placeholder="promo2025, newyear" />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>📝 Заметки</label>
                    <textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} placeholder="Комментарии к лиду..." rows={2} />
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={closeModal}>Отмена</button>
              <button className="btn-primary" onClick={saveContact} disabled={!isFormValid() || uploading}>
                {uploading ? 'Сохранение...' : (editingContact ? 'Сохранить' : 'Добавить')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal - Import CSV */}
      {showImportModal && (
        <div className="modal-overlay" onClick={closeImportModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📥 Импорт из CSV</h2>
              <button className="modal-close" onClick={closeImportModal}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="import-info">
                <p>Загрузите CSV файл с контактами для раздела <strong>{config.title}</strong></p>
                <button className="btn-link" onClick={downloadTemplate}>📄 Скачать шаблон CSV</button>
              </div>
              
              <div className="import-upload">
                <input 
                  ref={csvInputRef}
                  type="file" 
                  accept=".csv,.txt" 
                  onChange={handleCsvFile}
                  id="csv-input"
                  style={{ display: 'none' }}
                />
                <label htmlFor="csv-input" className="upload-area">
                  {csvContent ? (
                    <div className="upload-ready">
                      <span className="upload-icon">✅</span>
                      <span>Файл загружен</span>
                      <small>{csvContent.split('\n').length - 1} строк</small>
                    </div>
                  ) : (
                    <div className="upload-prompt">
                      <span className="upload-icon">📁</span>
                      <span>Нажмите для выбора CSV</span>
                      <small>Поддержка: .csv, .txt (UTF-8)</small>
                    </div>
                  )}
                </label>
              </div>
              
              {csvContent && !importResult && (
                <div className="csv-preview">
                  <h4>Превью (первые 5 строк):</h4>
                  <pre>{csvContent.split('\n').slice(0, 6).join('\n')}</pre>
                </div>
              )}
              
              {importResult && (
                <div className="import-result">
                  <h4>✅ Импорт завершён</h4>
                  <div className="result-stats">
                    <div className="result-stat success">
                      <span className="stat-num">{importResult.imported}</span>
                      <span>добавлено</span>
                    </div>
                    <div className="result-stat warning">
                      <span className="stat-num">{importResult.updated}</span>
                      <span>обновлено</span>
                    </div>
                    <div className="result-stat error">
                      <span className="stat-num">{importResult.skipped}</span>
                      <span>пропущено</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <div className="modal-footer">
              <button className="btn-secondary" onClick={closeImportModal}>
                {importResult ? 'Закрыть' : 'Отмена'}
              </button>
              {!importResult && (
                <button className="btn-primary" onClick={importCsv} disabled={!csvContent || importing}>
                  {importing ? 'Импорт...' : '📥 Импортировать'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessContacts;